import {
  DueDisplayOptions,
  Task,
  Timeslot,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  getScheduleCompletedOnDate,
  isNumber,
  shouldDueBeShown,
} from '@griply/utils';
import { isBefore, isSameDay, startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { useActiveSchedule } from './use-active-schedule';
import { useFutureOccurrence } from './use-future-occurrence';
import { useTaskPlannedOccurrenceForDate } from './use-task-planned-occurrence-for-date';

const allDueDisplayOptions = Object.values(DueDisplayOptions);

export type UseTaskDueAndTimeslotOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
  displayDate?: Timestamp;
  displayDue?: DueDisplayOptions[];
};

export type TaskDueAndTimeslot = {
  dueDate: Timestamp | undefined;
  timeslot: Timeslot | null | undefined;
  hasTimeslot: boolean;
};

export const useTaskDueAndTimeslot = (
  task: Task,
  {
    today,
    weekStartsOn,
    displayDate,
    displayDue = allDueDisplayOptions,
  }: UseTaskDueAndTimeslotOptions,
): TaskDueAndTimeslot => {
  const activeSchedule = useActiveSchedule(task.schedules, today);

  const plannedOccurrenceForDate = useTaskPlannedOccurrenceForDate(
    task,
    displayDate ?? today,
    { weekStartsOn, today },
  );

  const plannedOccurrence = useMemo(() => {
    if (!plannedOccurrenceForDate || !activeSchedule) {
      return plannedOccurrenceForDate;
    }

    const isScheduleCompleted = getScheduleCompletedOnDate(
      activeSchedule,
      plannedOccurrenceForDate.date,
      { fullyCompleted: true },
    );

    if (isScheduleCompleted) {
      return undefined;
    }

    const isPlannedOccurrenceInActiveSchedule =
      activeSchedule.plannedOccurrences?.find((occurrence) =>
        isSameDay(occurrence.date, plannedOccurrenceForDate.date),
      );

    if (isPlannedOccurrenceInActiveSchedule) {
      return plannedOccurrenceForDate;
    }

    if (
      activeSchedule.startDate &&
      (isSameDay(
        plannedOccurrenceForDate.date,
        startOfDay(activeSchedule.startDate),
      ) ||
        !isBefore(
          plannedOccurrenceForDate.date,
          startOfDay(activeSchedule.startDate),
        ))
    ) {
      return plannedOccurrenceForDate;
    }

    return undefined;
  }, [activeSchedule, plannedOccurrenceForDate]);

  const futureOccurrence = useFutureOccurrence(task, {
    plannedOccurrence,
    displayDue,
    displayDate,
    today,
    weekStartsOn,
  });

  return useMemo(() => {
    const plannedOccurrenceShown =
      plannedOccurrence?.date ?? futureOccurrence?.date;
    const fallbackDeadline =
      !plannedOccurrenceShown &&
      !task.schedules?.length &&
      task.endStrategy?.deadline
        ? task.endStrategy.deadline
        : undefined;

    const dueDate =
      (plannedOccurrenceShown &&
        shouldDueBeShown(plannedOccurrenceShown, displayDue, today)) ||
      (fallbackDeadline &&
        shouldDueBeShown(fallbackDeadline, displayDue, today))
        ? plannedOccurrence?.date ?? futureOccurrence?.date ?? fallbackDeadline
        : undefined;

    const timeslot = plannedOccurrence
      ? plannedOccurrence.timeslot
      : activeSchedule
        ? activeSchedule.timeslot
        : task.timeslot;

    const hasTimeslot =
      !!timeslot && (isNumber(timeslot.startTime) || !!timeslot.duration);

    return { dueDate, timeslot, hasTimeslot };
  }, [
    activeSchedule,
    displayDue,
    futureOccurrence,
    plannedOccurrence,
    task.endStrategy?.deadline,
    task.schedules?.length,
    task.timeslot,
    today,
  ]);
};
